import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import {
  useCreateClientFieldMutation,
  useDeleteClientFieldMutation,
  useEditClientFieldMutation,
  useGetClientFieldsQuery,
  useLazyGetClientFieldByIdQuery,
  type ModelClientField,
} from '@app/api/ClientFields';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants/alerts';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { ClientFieldFormValues } from '../types';

// The additional-fields registry has no dedicated access-right node yet, so it
// reuses the Clients module: viewing follows client visibility, and mutations
// follow the client create/edit/delete rights.
export const useClientFields = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const {
    userAccessRights: { Clients },
  } = useNewUserAccessRights();
  const { showNotification } = useNotification();
  const { isMobile } = useWindowSize();

  const [form] = Form.useForm<ClientFieldFormValues>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);

  const isBranchReady = Boolean(ActiveBranchId);

  const { data: fields = [], isFetching } = useGetClientFieldsQuery(
    { ActiveBranchId },
    { skip: !isBranchReady }
  );

  const [createClientField, { isLoading: isCreating }] =
    useCreateClientFieldMutation();
  const [editClientField, { isLoading: isEditing }] =
    useEditClientFieldMutation();
  const [deleteClientField, { isLoading: isDeleting }] =
    useDeleteClientFieldMutation();
  const [getClientFieldById, { isFetching: isEditingFieldLoading }] =
    useLazyGetClientFieldByIdQuery();

  const canView = Boolean(Clients?.IsOn);
  const canEdit = Boolean(Clients?.IsEdit);
  const canDelete = Boolean(Clients?.IsDelete);

  const editingField = useMemo(
    () => fields.find((field) => field.Id === editingFieldId) ?? null,
    [fields, editingFieldId]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingFieldId(null);
    form.resetFields();
  }, [form]);

  const openCreateModal = useCallback(() => {
    setEditingFieldId(null);
    form.resetFields();
    setIsModalOpen(true);
  }, [form]);

  // GetClientFields не отдаёт IsImportant (всегда false), поэтому начальные
  // значения формы дочитываем по GetClientFieldById — иначе сохранение
  // затирало бы признак «важное» у поля, которому его уже поставили.
  const openEditModal = useCallback(
    async (field: ModelClientField) => {
      setEditingFieldId(field.Id);
      form.setFieldsValue({
        Name: field.Name ?? '',
        IsDeleted: field.IsDeleted,
        IsImportant: field.IsImportant ?? false,
      });
      setIsModalOpen(true);

      if (!ActiveBranchId) return;

      try {
        const actualField = await getClientFieldById({
          ActiveBranchId,
          fieldId: field.Id,
        }).unwrap();

        form.setFieldsValue({
          Name: actualField.Name ?? '',
          IsDeleted: actualField.IsDeleted,
          IsImportant: actualField.IsImportant ?? false,
        });
      } catch (error) {
        showNotification(error, WARNING);
      }
    },
    [ActiveBranchId, form, getClientFieldById, showNotification]
  );

  const submitForm = useCallback(async () => {
    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    const values = await form.validateFields();
    const name = values.Name.trim();

    try {
      if (editingFieldId !== null) {
        await editClientField({
          ActiveBranchId,
          data: {
            Id: editingFieldId,
            Name: name,
            IsDeleted: values.IsDeleted ?? false,
            IsImportant: values.IsImportant ?? false,
          },
        }).unwrap();
        showNotification('Поле обновлено', SUCCESS);
      } else {
        await createClientField({
          ActiveBranchId,
          data: {
            Id: 0,
            Name: name,
            IsDeleted: false,
            IsImportant: values.IsImportant ?? false,
          },
        }).unwrap();
        showNotification('Поле создано', SUCCESS);
      }

      closeModal();
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [
    ActiveBranchId,
    closeModal,
    createClientField,
    editClientField,
    editingFieldId,
    form,
    showNotification,
  ]);

  const deleteField = useCallback(
    async (field: ModelClientField) => {
      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан', FRONT_WARNING);
        return;
      }

      try {
        await deleteClientField({ ActiveBranchId, fieldId: field.Id }).unwrap();
        showNotification('Поле удалено', SUCCESS);
      } catch (error) {
        showNotification(error, WARNING);
      }
    },
    [ActiveBranchId, deleteClientField, showNotification]
  );

  return {
    fields,
    isLoading: isFetching,
    isEditingFieldLoading,
    isSubmitting: isCreating || isEditing,
    isDeleting,
    isModalOpen,
    editingField,
    form,
    canView,
    canEdit,
    canDelete,
    isMobile,
    openCreateModal,
    openEditModal,
    closeModal,
    submitForm,
    deleteField,
    setIsModalOpen,
  };
};
